import { z } from 'zod';
import { TranscriptScanDirection, TranscriptSortOrder } from '../types/apiTypes';
import { ValidatorUtils } from '../utils/ValidatorUtils';
import { MessageParticipantRole, MessageType } from '../types/sharedTypes';
import { ContentTypeEnumSchema, JsonContentTypeEnumSchema, MessageAttributesSchema } from './sharedSchemas';

export const ConnectionRequestSchema = z.object({
    sessionId: z.string().uuid().optional(),
});

export const SendEventContentTypeSchema = ContentTypeEnumSchema.extract([
    'application/vnd.amazonaws.connect.event.typing',
    'application/vnd.amazonaws.connect.event.message.read',
    'application/vnd.amazonaws.connect.event.message.delivered',
]);

export const SendJsonContentTypeSchema = JsonContentTypeEnumSchema.extract([
    'application/chat.event.feedback.thumbs.up',
    'application/chat.event.feedback.thumbs.down',
    'application/chat.event.end.request',
]);

const ReceiptSchema = z.object({
    deliveredTimestamp: z.number().int().positive().max(8640000000000000),
    readTimestamp: z.number().int().positive().max(8640000000000000).optional(),
    recipientParticipantRole: z.nativeEnum(MessageParticipantRole),
});

export const MessageMetadataSchema = z.object({
    messageId: z.string().uuid(),
    receipts: ReceiptSchema.array(),
});

export const EgressJsonContentSchema = z.object({
    type: SendJsonContentTypeSchema,
    attributes: z.record(z.string(), z.string()).optional(),
});

export const JsonContentSchema = z.object({
    type: JsonContentTypeEnumSchema,
    attributes: z.record(z.string(), z.string()).optional(),
    response: z.string().optional(),
    products: z.object({
        desc: z.string(),
        imageUrl: z.string(),
        markdownPrice: z.string().optional(),
        price: z.string(),
        slug: z.string(),
        title: z.string(),
    }).array().optional(),
    sources: z.object({
        title: z.string(),
        url: z.string(),
    }).array().optional(),
    events: z.object({
        eventId: z.string(),
    }).array().optional(),
    doc: z.record(z.string(), z.any()).optional(),
});

export const ReceiptJsonContentSchema = z.object({
    messageId: z.string().uuid(),
});

export type JsonContent = z.infer<typeof JsonContentSchema>;

export const SendMessageRequestSchema = z.object({
    sessionId: z.string().uuid().optional(),
    message: z.union([
        z.object({
            type: z.literal(MessageType.MESSAGE),
            content: z.pipeline(
                z.string().transform((s) => ValidatorUtils.sanitizeString(s)),
                z.string().min(1).max(1024),
            ),
            contentType: ContentTypeEnumSchema.extract(['text/plain']),
            attributes: MessageAttributesSchema.optional(),
        }),
        z.object({
            type: z.literal(MessageType.MESSAGE),
            content: z.string().min(1).max(1200)
                .refine(
                    (s) => {
                        try {
                            const parsed = JSON.parse(s);
                            const { success } = EgressJsonContentSchema.safeParse(parsed);
                            return success;
                        } catch (e) {
                            return false;
                        }
                    },
                    {
                        message: 'Invalid JSON content',
                    },
                ),
            contentType: ContentTypeEnumSchema.extract(['application/json']),
        }),
        z.object({
            type: z.literal(MessageType.EVENT),
            content: z.string().max(16384)
                .refine(
                    (s) => {
                        try {
                            const parsed = JSON.parse(s);
                            const { success } = ReceiptJsonContentSchema.safeParse(parsed);
                            return success;
                        } catch (e) {
                            return false;
                        }
                    },
                    {
                        message: 'Invalid JSON content',
                    },
                ),
            contentType: SendEventContentTypeSchema.extract([
                'application/vnd.amazonaws.connect.event.message.read',
                'application/vnd.amazonaws.connect.event.message.delivered',
            ]),
        }),
        z.object({
            type: z.literal(MessageType.EVENT),
            contentType: SendEventContentTypeSchema.extract([
                'application/vnd.amazonaws.connect.event.typing',
            ]),
        }),
    ]),
});

export const EndRequestSchema = z.object({
    sessionId: z.string().uuid().optional(),
});

export const TranscriptRequestSchema = z.object({
    sessionId: z.string().uuid().optional(),
    scanDirection: z.nativeEnum(TranscriptScanDirection).optional(),
    sortOrder: z.nativeEnum(TranscriptSortOrder).optional(),
    maxResults: z.number().min(1).max(100).optional(),
    nextToken: z.string().uuid().optional(),
});

export const WsConnectRequestSchema = z.object({
    identityId: z.string().min(40).max(100)
        .transform((s) => ValidatorUtils.sanitizeString(s)),
});
